<template>
    <Dialog>
        <DialogTrigger>
            <slot></slot>
        </DialogTrigger>
        <DialogContent class="max-w-5xl">
            <DialogHeader>
                <DialogTitle>
                    {{ $t("integrations.add_integration") }}
                </DialogTitle>
                <DialogDescription>
                    <!-- Přidat integraci do vašeho kalendáře -->
                </DialogDescription>
            </DialogHeader>

            <div class="grid grid-cols-3 gap-3">
                <IntegrationCard :requires-sub="false">
                    <template #logo>
                        <GoogleLogo />
                    </template>

                    <template #name>
                        {{ $t("integrations.google.calendar") }}
                    </template>

                    <template #description>
                        {{ $t("integrations.google.description") }}
                    </template>

                    <template #cta>
                        <Button
                            as="a"
                            variant="outline"
                            class="mt-4 gap-3"
                            href="/integrations/google/auth-form"
                        >
                            <GoogleLogo />
                            {{ $t("integrations.google.sign_in") }}
                        </Button>
                    </template>
                </IntegrationCard>

                <ICloudCalendarIntegrationModal>
                    <IntegrationCard :requires-sub="false">
                        <template #logo>
                            <ICloudLogo />
                        </template>

                        <template #name>
                            {{ $t("integrations.icloud_calendar.title") }}
                        </template>

                        <template #description>
                            {{ $t("integrations.icloud_calendar.description") }}
                        </template>
                    </IntegrationCard>
                </ICloudCalendarIntegrationModal>

                <EcomailIntegrationModal>
                    <IntegrationCard requires-sub :has-sub="isSubscribed">
                        <template #logo>
                            <EcomailLogo />
                        </template>

                        <template #name>
                            {{ $t("integrations.ecomail.title") }}
                        </template>

                        <template #description>
                            {{ $t("integrations.ecomail.description") }}
                        </template>
                    </IntegrationCard>
                </EcomailIntegrationModal>

                <CloseIntegrationModal>
                    <IntegrationCard requires-sub :has-sub="isSubscribed">
                        <template #logo>
                            <CloseLogo />
                        </template>

                        <template #name>
                            {{ $t("integrations.close.title") }}
                        </template>

                        <template #description>
                            {{ $t("integrations.ecomail.description") }}
                        </template>
                    </IntegrationCard>
                </CloseIntegrationModal>

                <SlackIntegrationModal>
                    <IntegrationCard requires-sub :has-sub="isSubscribed">
                        <template #logo>
                            <SlackLogo />
                        </template>

                        <template #name>
                            {{ $t("integrations.slack.title") }}
                        </template>

                        <template #description>
                            {{ $t("integrations.slack.description") }}
                        </template>
                    </IntegrationCard>
                </SlackIntegrationModal>

                <RaynetIntegrationModal>
                    <IntegrationCard requires-sub :has-sub="isSubscribed">
                        <template #logo>
                            <img
                                class="-mt-1 h-8"
                                src="https://d39xau2r7ekvda.cloudfront.net/application/assets/img/Raynet.png"
                            />
                        </template>

                        <template #name>
                            {{ $t("integrations.raynet.title") }}
                        </template>

                        <template #description>
                            {{ $t("integrations.raynet.description") }}
                        </template>
                    </IntegrationCard>
                </RaynetIntegrationModal>

                <FreeloIntegrationModal>
                    <IntegrationCard requires-sub :has-sub="isSubscribed">
                        <template #logo>
                            <FreeloLogo />
                        </template>

                        <template #name>
                            {{ $t("integrations.freelo.title") }}
                        </template>

                        <template #description>
                            {{ $t("integrations.freelo.description") }}
                        </template>
                    </IntegrationCard>
                </FreeloIntegrationModal>

                <IntegrationCard requires-sub :has-sub="isSubscribed">
                    <template #logo>
                        <StripeLogo class="w-16" />
                    </template>

                    <template #name> </template>

                    <template #description>
                        {{ $t("integrations.stripe.description") }}
                    </template>

                    <template #cta>
                        <div v-if="!isSubscribed" class="relative mt-5 gap-3">
                            <Button
                                type="button"
                                class="w-full"
                                variant="outline"
                                @click.prevent="showProPayment"
                            >
                                {{ $t("add") }}</Button
                            >
                            <svg
                                class="absolute right-2 top-1"
                                width="16"
                                height="16"
                                viewBox="0 0 16 16"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M3.33319 13.3332H12.6665M1.33319 2.6665L3.33319 10.6665H12.6665L14.6665 2.6665L10.6665 7.33317L7.99986 2.6665L5.33319 7.33317L1.33319 2.6665Z"
                                    stroke="url(#paint0_linear_1067_2699)"
                                    stroke-width="1.4"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                />
                                <defs>
                                    <linearGradient
                                        id="paint0_linear_1067_2699"
                                        x1="1.00018"
                                        y1="1.99976"
                                        x2="10.1364"
                                        y2="14.5624"
                                        gradientUnits="userSpaceOnUse"
                                    >
                                        <stop stop-color="#FF8D47" />
                                        <stop
                                            offset="0.329612"
                                            stop-color="#FF8947"
                                        />
                                        <stop
                                            offset="0.534043"
                                            stop-color="#FF4247"
                                        />
                                        <stop
                                            offset="0.68692"
                                            stop-color="#E92A5B"
                                        />
                                        <stop
                                            offset="0.828171"
                                            stop-color="#DD1C67"
                                        />
                                        <stop offset="1" stop-color="#CF0283" />
                                    </linearGradient>
                                </defs>
                            </svg>
                        </div>
                        <Button
                            v-if="isSubscribed"
                            :disabled="loading"
                            variant="outline"
                            class="mt-4 gap-3"
                            @click="addStripeIntegration"
                        >
                            {{ $t("add") }}
                        </Button>
                    </template>
                </IntegrationCard>
            </div>

            <DialogClose>
                <div class="flex w-full justify-end">
                    <Button type="button" variant="outline">
                        {{ $t("close") }}
                    </Button>
                </div>
            </DialogClose>
        </DialogContent>
    </Dialog>
</template>

<script lang="ts" setup>
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogClose,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";

import { Button } from "@/components/ui/button";
import IntegrationCard from "./IntegrationCard.vue";
import axios from "axios";
import route from "ziggy-js";
import useProDialog from "@/ProModal/composables/useProDialog";

// Integration Modals
import CloseIntegrationModal from "./CloseIntegrationModal.vue";
import EcomailIntegrationModal from "./EcomailIntegrationModal.vue";
import SlackIntegrationModal from "./SlackIntegrationModal.vue";
import RaynetIntegrationModal from "./RaynetIntegrationModal.vue";
import FreeloIntegrationModal from "./FreeloIntegrationModal.vue";
import ICloudCalendarIntegrationModal from "./ICloudCalendarIntegrationModal.vue";
// import GoogleCalendarIntegrationModal from "./GoogleCalendarIntegrationModal.vue";

// Integration Logos
import CloseLogo from "./logos/Close.svg";
import EcomailLogo from "./logos/Ecomail.svg";
import SlackLogo from "./logos/Slack.svg";
import FreeloLogo from "./logos/Freelo.svg";
import ICloudLogo from "./logos/ICloud.svg";
import GoogleLogo from "./logos/Google.svg";
import StripeLogo from "./logos/Stripe.svg";

import { ref } from "vue";

import { useHasSubscription } from "@/composables/useHasSubscription";

const { hasSubscription: isSubscribed } = useHasSubscription();

const loading = ref(false);

const proDialog = useProDialog();

function showProPayment() {
    proDialog.show({
        feature: "integrations",
    });
}

async function addStripeIntegration() {
    if (loading.value) return;

    loading.value = true;

    const response = await axios.post(route("app.stripe-integrations.store"));

    if (response.data.success) {
        window.location.href = response.data.data.url;
    } else {
        loading.value = false;
    }
}
</script>
